import React, { memo, useEffect, useMemo, useState } from 'react';
import cn from 'classnames';
import { useDrag } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';

import { IDraggableEvent } from '~/Screens/Calendar/interfaces';
import { PositionedEvent } from '~/Screens/Calendar/CalendarView/components/PositionedEvent';
import { isPast } from 'date-fns';
import { useStyles } from '~/Screens/Calendar/CalendarView/components/DraggableEvent/DraggableEvent.styles';

export const DraggableEvent = memo((props: IDraggableEvent) => {
  const { classes } = useStyles();
  const { position, event, type } = props;
  const [time, setTime] = useState(() => new Date());
  const isEventInPast = useMemo(() => event && isPast(Date.parse(event.start)), [
    event?.start,
    time,
  ]);
  const canDrag = useMemo(() => !event.cancelled && !isEventInPast, [
    event.cancelled,
    isEventInPast,
  ]);
  const [{ isDragging }, drag, preview] = useDrag(
    () => ({
      type,
      item: props,
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
      canDrag,
    }),
    [props, canDrag],
  );

  useEffect((): void | (() => void) => {
    if (isEventInPast || isDragging) {
      return;
    }
    const timeInterval = setInterval(() => {
      if (isPast(Date.parse(event.start))) {
        setTime(new Date());
      }
    }, 10000);
    return () => clearInterval(timeInterval);
  }, [event.start, time, isEventInPast, isDragging]);

  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
  }, []);

  return (
    <section
      ref={drag}
      role="DraggableBox"
      className={cn(classes.container, { [classes.canDrag]: canDrag })}
    >
      <PositionedEvent
        event={event}
        isPast={isEventInPast}
        position={position}
        isDisabled={isDragging}
        withTooltip
      />
    </section>
  );
});
